import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Globe, Search, Hammer, LineChart, ExternalLink, ArrowRight, FlaskConical } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] } }),
};

const CountUp = ({ end, decimals = 0, duration = 1500 }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started.current) {
        started.current = true;
        const t0 = performance.now();
        const tick = (now) => {
          const p = Math.min((now - t0) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setVal(end * eased);
          if (p < 1) requestAnimationFrame(tick);
          else setVal(end);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{val.toFixed(decimals)}</span>;
};

const impactStats = [
  { end: 1.1, decimals: 1, suffix: "M+", label: "SDG learners reached" },
  { end: 400, decimals: 0, suffix: "K", label: "USD secured · 20-country delivery" },
  { end: 5.0, decimals: 1, suffix: "/5", label: "UN 360° innovation rating" },
];

const caseStudies = [
  {
    context: "UN DCO · Resident Coordinators · system-wide",
    title: "Socializing the UN's most senior country leaders around the 2030 Agenda",
    problem: "Resident Coordinators lead the UN's country teams and coordinate its support to the 2030 Agenda. DCO had just published a Foundational Primer on the Agenda — and needed a way to socialize the RCs around it.",
    move: "Built an online journey on that Primer: a short assessment, a 700-word essay answering an RC-specific question, peer comments on two colleagues' essays, and a one-minute video on their own country context. Delivered on un.org and handed to DCO to run.",
    metric: "60%+ of all Resident Coordinators completed it",
    links: [{ label: "Cited in the 2020 Report of the Chair of the UNSDG", url: "https://unsdg.un.org/resources/report-unsdg-chair-development-coordination-office" }],
  },
  {
    context: "UN Thailand · bilingual EN/TH · replicated across agencies",
    title: "Turning a UN Country Team into a producer of learning",
    problem: "National SDG awareness in Thailand was low, and Country Teams were treated as recipients of global learning rather than authors of their own.",
    move: "Co-designed a culturally grounded bilingual micro-primer with UN Thailand and launched it at the Sustainability Expo in Bangkok — a model other UN agencies then replicated.",
    metric: "12,700+ learners · national SDG awareness up from 17% to 25% (2023–2025)",
    links: [{ label: "Read the UNSSC case story", url: "https://www.unssc.org/case-story-inspiring-youth-thailand-act-sdgs" }],
  },
  {
    context: "Africa Sustainable Development Academy · 20 countries · RC-nominated",
    title: "Proving capacity building changed what institutions do",
    problem: "Capacity programmes almost never show whether anything changed once participants go home — the evidence stops at the completion certificate.",
    move: "Led the inaugural Academy across 20 African countries, with AI-supported audio for a participant who is blind and mobile-first design for low connectivity — and built four-month and one-year follow-ups in from the start.",
    metric: "99% recommendation · participants reforming processes and shifting policy a year on",
    links: [
      { label: "Turning advocacy into action — Tebogo Suping", url: "https://www.unssc.org/news-and-insights/interview/turning-advocacy-action-spotlight-interview-tebogo-suping" },
      { label: "Championing change — Ahmed Malide & Hayad Robleh", url: "https://www.unssc.org/news-and-insights/interview/championing-change-spotlight-interview-ahmed-malide-and-hayad-robleh" },
    ],
  },
];

const pillars = [
  {
    Icon: Search,
    num: "01 · Diagnose",
    title: "Construct the problem before the solution",
    text: "The work starts by unpacking a vague sense of failure into a specific performance gap someone can actually act on — not by reaching for a platform or a curriculum. Surface the workflows: who decides, who delivers, who is accountable to whom. Most programme failure stays invisible until it is mapped that way, and only then does the distance between a decision on paper and a change in practice come into view.",
  },
  {
    Icon: Hammer,
    num: "02 · Build",
    title: "Build the instrument that closes the gap",
    text: "A diagnosis changes nothing until it reaches people. Learning programmes delivered across 100+ countries; certification designed for the UN's most senior country representatives; formats built for limited connectivity and for participants using assistive technology — carrying real delivery and donor accountability, not just design.",
  },
  {
    Icon: LineChart,
    num: "03 · Prove",
    title: "Measure past the certificate",
    text: "What matters is what changed months later, not how many people finished. Four-month and one-year follow-ups show whether a process was reformed or a national policy conversation shifted. Evidence sits across the full capability chain — from reach, to application, to institutional and societal change.",
  },
];

const Card = ({ i, slug, onOpen, className = "work-card", children, testid }) => (
  <motion.div
    className={className}
    data-testid={testid || `work-card-${slug}`}
    variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i}
  >
    {children}
  </motion.div>
);

export default function WorkTab({ onGoInnovation }) {
  const onOpen = () => {};
  return (
    <div className="tab-panel" data-testid="tab-panel-work">
      <div className="panel-wrap cream">
        <div className="impact-nums" data-testid="impact-numbers">
          <div className="eyebrow">Impact by the numbers</div>
          <div className="impact-stats">
            {impactStats.map((s, i) => (
              <motion.div className="impact-stat" key={i} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i} data-testid={`impact-stat-${i}`}>
                <div className="impact-stat-num"><CountUp end={s.end} decimals={s.decimals} /><sup>{s.suffix}</sup></div>
                <div className="impact-stat-lbl">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="eyebrow">The through-line</div>
        <h2 className="heading">Programmes rarely fail<br />in the design.</h2>
        <div className="arg" data-testid="work-argument">
          <p className="arg-lead">They fail somewhere between the decision and the practice it was meant to change. Approvals pile up, the people delivering it are already stretched, and those a programme is meant to help are rarely asked before it is built. <strong>Closing that gap is the work.</strong></p>
          <p className="arg-sub">For twenty years — from the Philippine Congress to UN platforms across the system — Paulyn has worked on why well-funded, well-designed sustainable-development programmes stall on the way to results, and built the interventions that close the distance. Learning and knowledge are the instruments. Results are the point.</p>
        </div>

        <div className="section-head" data-testid="impact-head">
          <div className="eyebrow">Key impact</div>
          <h3 className="section-h">Three programmes, three kinds of proof</h3>
          <p className="section-intro">Not a task list — the problems these programmes solved, and the external evidence that they worked. Each one led with a question about results, then chose the instrument to match.</p>
        </div>
        <div className="case-grid" data-testid="case-grid">
          {caseStudies.map((c, i) => (
            <motion.div className="case" key={i} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i} data-testid={`case-${i}`}>
              <div className="case-eyebrow">{c.context}</div>
              <div className="case-title">{c.title}</div>
              <p className="case-line"><span className="case-tag">The problem</span> {c.problem}</p>
              <p className="case-line"><span className="case-tag">The move</span> {c.move}</p>
              <div className="case-metric">{c.metric}</div>
              <div className="case-links">
                {c.links.map((l, j) => (
                  <a className="case-link" href={l.url} target="_blank" rel="noopener noreferrer" key={j} data-testid={`case-${i}-link-${j}`}>
                    <ExternalLink className="icon" /> {l.label}
                  </a>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="section-head" data-testid="method-head">
          <div className="eyebrow">How I work</div>
          <h3 className="section-h">Diagnose. Build. Prove.</h3>
          <p className="section-intro">Across twenty years the method has stayed the same, whatever the instrument — a repeatable way of moving from a stated problem to evidence that something actually changed.</p>
        </div>
        <div className="arg-pillars" data-testid="work-pillars">
          {pillars.map(({ Icon, num, title, text }, i) => (
            <motion.div className="arg-pillar" key={i} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i} data-testid={`work-pillar-${i}`}>
              <div className="arg-pillar-icon"><Icon className="icon" /></div>
              <div className="arg-pillar-num">{num}</div>
              <div className="arg-pillar-title">{title}</div>
              <div className="arg-pillar-text">{text}</div>
            </motion.div>
          ))}
        </div>

        <button className="method-cta" onClick={onGoInnovation} data-testid="work-tools-cta">
          <FlaskConical className="icon" />
          <span>
            <span className="method-cta-title">See the method in action</span>
            <span className="method-cta-sub">Two interactive decision tools — The Policy Function and The Learning Function — that put this approach in your hands</span>
          </span>
          <ArrowRight className="icon method-cta-arrow" />
        </button>

        <div className="arg-evidence-head">
          <div className="eyebrow">The full record</div>
          <p className="work-intro">Every initiative below started as a programme that had to convert resources into results — from a UN learning platform with 1.1 million users to virtual reality at the UN Climate Change Conference. Explore the thinking behind the work under <em>Thoughts</em>, and the person behind it under <em>About</em>.</p>
        </div>
        <div className="work-grid">

          <Card i={0} slug="un-sdg-learn" onOpen={onOpen} className="work-card featured" testid="work-card-sdglearn">
            <div className="wc-num">01</div>
            <div className="wc-icon"><Globe className="icon" /></div>
            <div className="wc-tag">Learning platform · UN system · 68 partners</div>
            <div className="wc-title">UN SDG:Learn initiative</div>
            <div className="wc-desc">Coordinator of the UN's flagship SDG learning platform, partnering with 68 organizations. Grew from 4,000 users in 2019 to 1.1 million cumulative users and 5 million pageviews by 2025. Drove 6.7 million LinkedIn impressions in 2023 through the "Free Courses" campaign.</div>
            <div className="wc-chips">
              <span className="wc-chip">1.1M+ users</span><span className="wc-chip">5M+ pageviews</span>
              <span className="wc-chip">50,200+ registered learners (2026)</span><span className="wc-chip">68 UN partners</span>
            </div>
          </Card>

          <Card i={1} slug="sdg-learncast" onOpen={onOpen}>
            <div className="wc-num">02</div>
            <div className="wc-tag">Podcast · Knowledge dissemination</div>
            <div className="wc-title">SDG Learncast podcast</div>
            <div className="wc-desc">Conceptualized, hosted, and produced across five seasons featuring Jeffrey Sachs, Benjamin Piper (Gates Foundation), Yasmine Sherif (ECW), and global leaders. Reached 100,000+ plays. Top 15% globally on Spotify in Education.</div>
            <div className="wc-chips">
              <span className="wc-chip">100K+ plays</span><span className="wc-chip">Top 15% Spotify Education</span><span className="wc-chip">59K LinkedIn followers</span>
            </div>
          </Card>

          <Card i={2} slug="asda" onOpen={onOpen}>
            <div className="wc-num">03</div>
            <div className="wc-tag">Capacity development · Africa</div>
            <div className="wc-title">Africa Sustainable Development Academy</div>
            <div className="wc-desc">Secured USD 400,000 in donor funding and positioned the Africa Sustainable Development Academy as a UNSSC flagship. Led the inaugural academy for participants across 20 African countries, each nominated by the UN Resident Coordinator in their country. Delivered AI-supported audio formats for a participant who is blind and a mobile-first design thinking workshop for those joining on limited connectivity. Participant evaluation returned a 99 per cent recommendation rate.</div>
            <div className="wc-chips">
              <span className="wc-chip">USD 400K secured</span><span className="wc-chip">99% recommendation rate</span><span className="wc-chip">20 countries</span>
            </div>
          </Card>

          <Card i={3} slug="inside-sustainable-development" onOpen={onOpen}>
            <div className="wc-num">04</div>
            <div className="wc-tag">Immersive learning · VR · 2020 &amp; 2025</div>
            <div className="wc-title">Inside Sustainable Development</div>
            <div className="wc-desc">Led UNSSC's first virtual reality film in 2020, built on the Know, Feel, Do framework. A 360-degree experience that places participants inside a city undergoing transformation. The 2025 revamp introduced stronger storytelling and two viewing modes: a linear full-length film, and a five-minute segmented pathway designed for modular workshops, conference booths and pavilions. Deployed at the UN Climate Change Conference in Sharm El-Sheikh and the Global Changemaker Academy for Parliamentarians, where it enabled a participant who uses a wheelchair to fully join a site visit. Also delivered as an online watch party for the UN Summer Academy from 2021 to 2023.</div>
            <div className="wc-chips">
              <span className="wc-chip">UN Climate Change Conference</span><span className="wc-chip">30 VR headsets</span><span className="wc-chip">Linear and segmented modes</span>
            </div>
          </Card>

          <Card i={4} slug="thai-micro-primer" onOpen={onOpen}>
            <div className="wc-num">05</div>
            <div className="wc-tag">Microlearning · Digital</div>
            <div className="wc-title">Thai SDG Micro-Primer</div>
            <div className="wc-desc">Co-designed with UN Thailand using a culturally grounded bilingual curriculum. Launched at the Sustainability Expo in Bangkok. Reached over 12,700 learners across English and Thai versions, contributing to a rise in national SDG awareness in Thailand from 17 per cent in 2023 to 25 per cent in 2025.</div>
            <div className="wc-chips">
              <span className="wc-chip">12,700+ learners</span><span className="wc-chip">National awareness 17% to 25%</span><span className="wc-chip">Bilingual EN/TH</span>
            </div>
          </Card>

          <Card i={5} slug="sdkb" onOpen={onOpen} className="work-card" testid="work-card-sdkb">
            <div style={{ borderTop: "3px solid var(--amber)", position: "absolute", inset: "0 0 auto 0" }}></div>
            <div className="wc-badge">In development</div>
            <div className="wc-num">06</div>
            <div className="wc-tag">Knowledge architecture · UNSSC-wide</div>
            <div className="wc-title">Sustainable Development Knowledge Bank</div>
            <div className="wc-desc">Originated the concept for a UNSSC-wide knowledge bank and co-developed it with a 22-volunteer working group from across the organization. Prototyped multi-format microlearning and knowledge summaries, and supported early prototyping of a learning game. Ran user research to test stakeholder assumptions and understand how people handle knowledge challenges within the UN. Currently in early development.</div>
            <div className="wc-chips">
              <span className="wc-chip">22-person working group</span><span className="wc-chip">Multi-format prototyping</span><span className="wc-chip">Early stage</span>
            </div>
          </Card>

          <Card i={6} slug="rc-certification" onOpen={onOpen}>
            <div className="wc-num">07</div>
            <div className="wc-tag">Certification · Resident Coordinators · UN DCO</div>
            <div className="wc-title">SDG certification for resident coordinators</div>
            <div className="wc-desc">Designed and delivered the SDG Certification platform in collaboration with UN DCO, targeting the UN's most senior country-level representatives. Over 60% of all Resident Coordinators completed the certification. Featured in the 2020 Report of the Chair of the UN Sustainable Development Group.</div>
            <div className="wc-chips">
              <span className="wc-chip">60%+ RC completion</span><span className="wc-chip">UN DCO partnership</span><span className="wc-chip">Cited in UNSDG 2020 report</span>
            </div>
          </Card>

          <Card i={7} slug="ai-vr" onOpen={onOpen}>
            <div style={{ borderTop: "3px solid var(--amber)", position: "absolute", inset: "0 0 auto 0" }}></div>
            <div className="wc-badge">In development</div>
            <div className="wc-num">08</div>
            <div className="wc-tag">AI · Virtual reality · Learning innovation</div>
            <div className="wc-title">AI-VR learning project on climate, land and biodiversity</div>
            <div className="wc-desc">Building immersive experiences that help leaders rehearse complex decisions. Develops the storyboard, scenarios and decision points, drawing on Common Country Analyses and evaluation reports to construct worlds grounded in real conditions. Also supported funding through the Programme Innovation Fund and drafted procurement instruments, including the Request for Information, Request for Proposal and terms of reference. In early development.</div>
            <div className="wc-chips">
              <span className="wc-chip">Storyboard and scenario design</span><span className="wc-chip">Decision-making under complexity</span><span className="wc-chip">Climate · Land · Biodiversity</span>
            </div>
          </Card>

          <Card i={8} slug="post-2015-hub" onOpen={onOpen}>
            <div className="wc-num">09</div>
            <div className="wc-tag">Digital platform · Knowledge management · 2014–2016</div>
            <div className="wc-title">Post-2015 Learning Hub</div>
            <div className="wc-desc">Designed the platform architecture and managed content for the Post-2015 Learning Hub — a curated digital learning resource for UN staff during the pivotal transition to the 2030 Agenda. Drafted the Director's speech for the platform launch alongside Amina Mohammed. Managed "SD Talks," a video library featuring senior UN officials explaining key sustainable development concepts.</div>
            <div className="wc-chips">
              <span className="wc-chip">Launched with Amina Mohammed</span><span className="wc-chip">SD Talks video library</span><span className="wc-chip">2030 Agenda transition</span>
            </div>
          </Card>

        </div>
      </div>
    </div>
  );
}
